/**
 * AGGridPlayground.tsx
 * Professional data visualization playground using AG Grid
 * 
 * Uses our shared UI components to showcase AG Grid integration with theme system
 */

import React, { useState, useMemo } from 'react';
import type { ColDef } from 'ag-grid-community';
import { PlaygroundPageLayout } from '../shared';
import { Card, CardHeader, CardBody, CardFooter } from '@enfomentis/ui';
import { ButtonGroup } from '@enfomentis/ui';
import { Badge } from '@enfomentis/ui';
import { Chip } from '@enfomentis/ui';
import { Tabs, Tab } from '@enfomentis/ui';
import { AGGridTable } from '@enfomentis/ui';
import { BarChart3, Zap, Settings, Activity } from 'lucide-react';


interface SampleTrade {
  id: string;
  symbol: string;
  side: 'buy' | 'sell';
  quantity: number;
  price: number;
  pnl: number;
  date: string;
  status: 'open' | 'closed';
}

const generateSampleTrades = (count: number): SampleTrade[] => {
  const symbols = ['AAPL', 'GOOGL', 'MSFT', 'TSLA', 'NVDA', 'AMZN', 'META', 'NFLX', 'AMD', 'INTC'];
  const sides: ('buy' | 'sell')[] = ['buy', 'sell'];
  const statuses: ('open' | 'closed')[] = ['open', 'closed'];
  
  const trades: SampleTrade[] = [];
  
  for (let i = 1; i <= count; i++) {
    const symbol = symbols[Math.floor(Math.random() * symbols.length)];
    const side = sides[Math.floor(Math.random() * sides.length)] as 'buy' | 'sell';
    const status = statuses[Math.floor(Math.random() * statuses.length)] as 'open' | 'closed';
    const quantity = Math.floor(Math.random() * 500) + 1;
    const price = Math.random() * 1000 + 10;
    const pnlMultiplier = Math.random() * 2 - 1;
    const pnl = Math.abs(pnlMultiplier) > 0.3 ? pnlMultiplier * quantity * 5 : 0;
    const daysAgo = Math.floor(Math.random() * 365);
    const tradeDate = new Date();
    tradeDate.setDate(tradeDate.getDate() - daysAgo);
    
    trades.push({
      id: `trade-${i.toString().padStart(4, '0')}`,
      symbol: symbol || 'UNKNOWN',
      side,
      quantity,
      price: Math.round(price * 100) / 100,
      pnl: Math.round(pnl * 100) / 100,
      date: tradeDate.toLocaleDateString(),
      status
    });
  }
  
  return trades;
};

export const AGGridPlayground: React.FC = () => {
  const [rowCount, setRowCount] = useState(100);

  const sampleData = useMemo(() => generateSampleTrades(rowCount), [rowCount]);

  const columnDefs: ColDef<SampleTrade>[] = [
    {
      field: 'id',
      headerName: 'Trade ID',
      width: 120,
      pinned: 'left'
    },
    {
      field: 'symbol',
      headerName: 'Symbol',
      width: 100,
      cellRenderer: (params: { value: string }) => (
        <Chip size="sm" color="primary" variant="flat">{params.value}</Chip>
      )
    },
    {
      field: 'side',
      headerName: 'Side',
      width: 80,
      cellRenderer: (params: { value: 'buy' | 'sell' }) => (
        <Chip
          color={params.value === 'buy' ? 'success' : 'danger'}
          size="sm"
          variant="flat"
        >
          {params.value.toUpperCase()}
        </Chip>
      )
    },
    {
      field: 'quantity',
      headerName: 'Quantity',
      width: 100,
      type: 'numericColumn'
    },
    {
      field: 'price',
      headerName: 'Price',
      width: 100,
      type: 'numericColumn',
      valueFormatter: (params) => `$${params.value.toFixed(2)}`
    },
    {
      field: 'pnl',
      headerName: 'P&L',
      width: 100,
      type: 'numericColumn',
      valueFormatter: (params) => `$${params.value.toFixed(2)}`,
      cellClassRules: {
        'text-success': (params) => params.value > 0,
        'text-error': (params) => params.value < 0,
        'text-text-secondary': (params) => params.value === 0
      }
    },
    {
      field: 'date',
      headerName: 'Date',
      width: 120
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      cellRenderer: (params: { value: 'open' | 'closed' }) => (
        <Chip
          size="sm"
          color={params.value === 'open' ? 'warning' : 'success'}
          variant="dot"
        >
          {params.value}
        </Chip>
      )
    }
  ];

  const rowCountOptions = [
    { label: '100 rows', value: '100' },
    { label: '500 rows', value: '500' },
    { label: '1K rows', value: '1000' },
    { label: '5K rows', value: '5000' }
  ];

  return (
    <PlaygroundPageLayout>
      <div className="mt-8">
        <Tabs variant="underlined" color="primary" size="lg" fullWidth>
          
          {/* Overview Tab */}
          <Tab 
            key="overview" 
            title={
              <div className="flex items-center gap-2">
                <BarChart3 size={16} />
                <span>Overview</span>
              </div>
            }
          >
            <div className="py-6">{/* Overview Section */}
        <div className="space-y-6">
          <Card shadow="lg" isHoverable>
            <CardHeader>
              <h3 className="text-xl font-semibold text-text-primary">AG Grid Integration</h3>
            </CardHeader>
            <CardBody>
              <div className="space-y-4">
                <p className="text-text-secondary">
                  Professional data grid with full theme integration, custom cell renderers using our shared components,
                  and high-performance virtual scrolling for large datasets.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardBody>
                      <Badge color="primary" content="∞" className="mb-2">
                        <div className="w-8 h-8 bg-primary/10 rounded-lg"></div>
                      </Badge>
                      <h4 className="font-semibold text-text-primary">Virtual Scrolling</h4>
                      <p className="text-sm text-text-secondary">Handle millions of rows efficiently</p>
                    </CardBody>
                  </Card>
                  
                  <Card>
                    <CardBody>
                      <Chip color="success" className="mb-2">Custom Renderers</Chip>
                      <h4 className="font-semibold text-text-primary">Shared Components</h4>
                      <p className="text-sm text-text-secondary">Badge, Chip, Button in cells</p>
                    </CardBody>
                  </Card>
                  
                  <Card>
                    <CardBody>
                      <Badge color="secondary" content="100%">
                        <div className="w-8 h-8 bg-secondary/10 rounded-lg"></div>
                      </Badge>
                      <h4 className="font-semibold text-text-primary">Theme Integration</h4>
                      <p className="text-sm text-text-secondary">Automatic dark mode support</p>
                    </CardBody>
                  </Card>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Quick Stats */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold text-text-primary">Sample Dataset Stats</h3>
            </CardHeader>
            <CardBody>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                <div>
                  <div className="text-2xl font-bold text-primary mb-1">{sampleData.length.toLocaleString()}</div>
                  <div className="text-sm text-text-secondary">Total Trades</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-success mb-1">
                    {sampleData.filter(t => t.pnl > 0).length}
                  </div>
                  <div className="text-sm text-text-secondary">Profitable</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-error mb-1">
                    {sampleData.filter(t => t.pnl < 0).length}
                  </div>
                  <div className="text-sm text-text-secondary">Losses</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-secondary mb-1">
                    {sampleData.filter(t => t.status === 'open').length}
                  </div>
                  <div className="text-sm text-text-secondary">Open Positions</div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
            </div>
          </Tab>

          {/* Basic Table Tab */}
          <Tab 
            key="basic" 
            title={
              <div className="flex items-center gap-2">
                <Settings size={16} />
                <span>Basic Table</span>
              </div>
            }
          >
            <div className="py-6">{/* Basic Table Section */}
        <div className="space-y-6">
          <Card shadow="lg" isHoverable>
            <CardHeader>
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-text-primary">Basic Data Table</h3>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-text-secondary">Rows:</span>
                  <ButtonGroup
                    options={rowCountOptions}
                    value={String(rowCount)}
                    onChange={(value) => setRowCount(Number(value))}
                    variant="flat"
                    size="sm"
                  />
                </div>
              </div>
            </CardHeader>
            <CardBody>
              <div className="h-96">
                <AGGridTable
                  rowData={sampleData}
                  columnDefs={columnDefs}
                  height="100%"
                  gridOptions={{
                    rowSelection: { mode: 'multiRow', checkboxes: false },
                    pagination: true,
                    paginationPageSize: 25
                  }}
                />
              </div>
            </CardBody>
            <CardFooter>
              <div className="flex justify-between items-center w-full">
                <p className="text-sm text-text-secondary">
                  {sampleData.length.toLocaleString()} trades • Theme-aware styling • Custom cell renderers
                </p>
                <span className="text-sm text-success">✨ Basic implementation complete</span>
              </div>
            </CardFooter>
          </Card>
        </div>
            </div>
          </Tab>

          {/* Interactive Tab */}
          <Tab 
            key="interactive" 
            title={
              <div className="flex items-center gap-2">
                <Activity size={16} />
                <span>Interactive</span>
              </div>
            }
          >
            <div className="py-6">{/* Interactive Section */}
        <div className="space-y-6">
          <Card shadow="lg" isHoverable>
            <CardHeader>
              <h3 className="text-xl font-semibold text-text-primary">Interactive Features</h3>
            </CardHeader>
            <CardBody>
              <div className="h-96">
                <AGGridTable
                  rowData={sampleData.slice(0, 100)} // Smaller set for interactive demo
                  columnDefs={columnDefs}
                  height="100%"
                  gridOptions={{
                    rowSelection: { mode: 'multiRow', checkboxes: true },
                    pagination: false,
                    onSelectionChanged: (event) => {
                      const selectedRows = event.api.getSelectedRows();
                      console.log('Selected trades:', selectedRows.length);
                    }
                  }}
                />
              </div>
            </CardBody>
            <CardFooter>
              <div className="flex items-center gap-3">
                <Chip size="sm" color="primary">Row Selection</Chip>
                <Chip size="sm" color="secondary">Sorting</Chip>
                <Chip size="sm" color="success">Filtering</Chip>
                <Chip size="sm" color="warning">Column Resize</Chip>
              </div>
            </CardFooter>
          </Card>
        </div>
            </div>
          </Tab>

          {/* Performance Tab */}
          <Tab 
            key="performance" 
            title={
              <div className="flex items-center gap-2">
                <Zap size={16} />
                <span>Performance</span>
              </div>
            }
          >
            <div className="py-6">{/* Performance Section */}
        <div className="space-y-6">
          <Card shadow="lg" isHoverable>
            <CardHeader>
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-text-primary">Performance Testing</h3>
                <Badge color="warning" content="High Performance">
                  <div className="w-8 h-8 bg-warning/10 rounded-lg"></div>
                </Badge>
              </div>
            </CardHeader>
            <CardBody>
              <p className="text-text-secondary mb-4">
                Test AG Grid performance with large datasets. Virtual scrolling ensures smooth performance
                regardless of dataset size.
              </p>
              
              <div className="mb-4">
                <ButtonGroup
                  options={rowCountOptions}
                  value={String(rowCount)}
                  onChange={(value) => setRowCount(Number(value))}
                  variant="solid"
                  size="sm"
                />
              </div>
              
              <div className="h-96">
                <AGGridTable
                  rowData={sampleData}
                  columnDefs={columnDefs}
                  height="100%"
                  gridOptions={{
                    rowSelection: { mode: 'multiRow', checkboxes: false },
                    pagination: false,
                    rowBuffer: 10,
                    suppressRowVirtualisation: false
                  }}
                />
              </div>
            </CardBody>
            <CardFooter>
              <div className="flex justify-between items-center w-full">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-text-secondary">
                    Rendering {sampleData.length.toLocaleString()} rows
                  </span>
                  <Chip size="sm" color="success">Virtual Scrolling</Chip>
                </div>
                <span className="text-sm text-warning">⚡ Optimized for large datasets</span>
              </div>
            </CardFooter>
          </Card>
        </div>
            </div>
          </Tab>
          
        </Tabs>
      </div>
    </PlaygroundPageLayout>
  );
};